import "../styles/Support.css";

function Support() {
  return (
    <main className="faq-page">
      <div className="container">
        <header className="faq-header">
          <h1>Frequently Asked Questions</h1>
          <p className="tagline">
            Everything you need to know about Bearer My Buddy — the Chrome
            extension for bearer token management (now on v2.1.0+).
          </p>
        </header>

        <section className="faq-section">
          <h2>General Questions</h2>

          <details>
            <summary>What is Bearer My Buddy?</summary>
            <p>
              Bearer My Buddy is a lightweight Chrome extension that
              automatically detects, collects, organizes, validates, and lets
              you copy bearer tokens from network requests. It saves developers,
              testers, and security analysts from manually digging through the
              Network tab in DevTools.
            </p>
          </details>

          <details>
            <summary>Is it really free?</summary>
            <p>
              Yes — completely free, no in-app purchases, no premium features
              locked behind paywalls. Just install and use.
            </p>
          </details>

          <details>
            <summary>Does it work on all websites?</summary>
            <p>
              It works on any site that uses bearer tokens in HTTP headers (most
              modern APIs, SPAs, etc.). It only activates on pages where it
              detects Authorization: Bearer headers in network requests.
            </p>
          </details>
        </section>

        <section className="faq-section">
          <h2>Privacy & Security</h2>

          {/* Existing privacy Q&As unchanged – they're perfect */}
          <details>
            <summary>Does Bearer My Buddy collect or send any data?</summary>
            <p>
              <strong>No.</strong> All tokens and data stay 100% local in your
              browser. The extension does not make any network requests, does
              not use telemetry, analytics, or external servers. Your tokens
              never leave your machine.
            </p>
          </details>

          <details>
            <summary>How are tokens stored?</summary>
            <p>
              Tokens are stored temporarily in Chrome's local storage
              (chrome.storage.local) — only while the browser is open. They are
              cleared when you clear browsing data or uninstall the extension.
              No encryption is applied (as it's short-lived session data), but
              everything is confined to your device.
            </p>
          </details>

          <details>
            <summary>What permissions does the extension require?</summary>
            <p>
              It requires:
              <ul>
                <li>
                  <code>webRequest</code> — to observe network requests and
                  headers
                </li>
                <li>
                  <code>webRequestBlocking</code> (optional, for future
                  features)
                </li>
                <li>
                  <code>storage</code> — to save the token list locally
                </li>
                <li>
                  Host permissions: <code>&lt;all_urls&gt;</code> (needed to
                  watch requests on any site)
                </li>
              </ul>
              These are standard for any extension that inspects network
              traffic.
            </p>
          </details>

          <details>
            <summary>Can someone steal my tokens using this extension?</summary>
            <p>
              No — the extension does not expose tokens to other websites,
              extensions, or scripts. Tokens are only visible in the popup UI
              when you open the extension icon. Chrome's extension isolation
              prevents cross-origin access.
            </p>
          </details>
        </section>

        <section className="faq-section">
          <h2>Features & Interface (v2.1.0+)</h2>

          <details>
            <summary>How do I switch to dark mode?</summary>
            <p>
              In the popup, check the "Dark Mode" box in the top controls. It persists across sessions via localStorage and overrides your system preference if needed.
            </p>
          </details>

          <details>
            <summary>What is auto-refresh and how do I use it?</summary>
            <p>
              Enable "Auto-refresh (every 10s)" in the popup controls. The token list updates live without clicking Refresh — great for monitoring token refreshes or long-running sessions.
            </p>
          </details>

          <details>
            <summary>How do I copy a token as a cURL command?</summary>
            <p>
              Next to each token, click the cURL/copy icon (often shown as {'>_'} or similar). It generates a full cURL command with the Bearer header included — paste directly into your terminal.
            </p>
          </details>

          <details>
            <summary>What is the "Env vars" button for?</summary>
            <p>
              Click "Env vars" (or the export button) to copy tokens in environment variable format (e.g., export MY_TOKEN=eyJhb... ). Handy for scripts, .env files, Postman, or CI workflows.
            </p>
          </details>

          <details>
            <summary>What are the filter dropdowns for?</summary>
            <p>
              Use the "Valid" dropdown to show only valid/expired/all tokens, and the "Domain" dropdown to group or filter by origin. Makes it easier to navigate large lists.
            </p>
          </details>

          <details open>
            <summary>How do I copy a token quickly?</summary>
            <p>
              Open the popup — each token has one-click copy buttons (plain token, full header, cURL command). Search/filter by domain, partial value, or status for fast access.
            </p>
          </details>
        </section>

        <section className="faq-section">
          <h2>Usage & Troubleshooting</h2>

          {/* Slightly updated existing ones + keep the rest */}
          <details>
            <summary>What do the validation badges mean?</summary>
            <p>
              <span className="status-badge valid">Green</span> = Token appears
              valid (not obviously malformed or expired based on JWT claims if
              present)
              <br />
              <span className="status-badge expired">Red</span> = Token is expired
              or invalid format
              <br />
              Gray/Opaque = Could not validate fully (e.g., session cookie style, not JWT)
            </p>
          </details>

          <details>
            <summary>Can I clear the token list?</summary>
            <p>
              Yes — use the "Clear" button (trash icon) in the popup to remove all tokens. You can also filter to show only expired and clear those specifically.
            </p>
          </details>

          <details>
            <summary>Why isn't a token showing up?</summary>
            <p>
              Common reasons:
              <ul>
                <li>
                  The request didn't include an{" "}
                  <code>Authorization: Bearer ...</code> header
                </li>
                <li>
                  The page uses a different auth method (cookies, basic auth,
                  etc.)
                </li>
                <li>
                  You need to reload the page or make a new request after
                  installing/updating
                </li>
                <li>Auto-refresh is off and you haven't clicked Refresh</li>
              </ul>
            </p>
          </details>
        </section>

        <section className="faq-section contact-section">
          <h2>Still have questions?</h2>
          <p>
            Feel free to reach out — I read every message.
            <br />
            <strong>Email:</strong>{" "}
            <a href="mailto:developer@pedrohma.com">developer@pedrohma.com</a>
            <br />
          </p>
          <p className="small">
            For bug reports, please include: browser version, steps to
            reproduce, and (if safe) a sample request header format.
          </p>
        </section>

        <footer>
          <p>
            © {new Date().getFullYear()} Pedro Arthur •{" "}
            <a href="/">Bearer My Buddy</a> • v2.1.0+
          </p>
        </footer>
      </div>
    </main>
  );
}

export default Support;